import * as Yup from "yup";
import {
  ConfiguredEventPointsMessageTemplates,
  EVENT_POINTS_MESSAGE_TEMPLATE_MAX_LENGTH,
  POINT_CURRENCY_NAME_MAX_LENGTH,
  PointCurrencyGender,
  PointSettings,
  REFERRAL_SHARE_TEXT_MAX_LENGTH,
  UpdatePointSettingsPayload,
} from "../../models/pointSettings";
import { SupportArticleListItem } from "../../models/support";
import { yupConstants } from "../../utils/constants/yup";

/**
 * Formulario de Configuración de puntos: nombre de los puntos (singular, plural y género) y el
 * artículo de Soporte de las reglas del canje. Valores iniciales, validación y payload del PUT,
 * sin render: así se testea directo.
 */
export interface PointSettingsFormValues {
  singular: string;
  plural: string;
  /** null = todavía no hay género elegido. */
  gender: PointCurrencyGender | null;
  rulesArticleId: string | null;
  eventPointsTitleTemplate: string;
  eventPointsAttendanceClauseTemplate: string;
  eventPointsBonusClauseTemplate: string;
  /** Vacío = usar el default del backend. */
  referralShareText: string;
}

/**
 * Arranca con el nombre que configuró el tenant. Sin nombre configurado los campos quedan vacíos
 * y el placeholder muestra el nombre por defecto, que es el que ve el usuario.
 */
export const buildInitialPointSettingsFormValues = (
  settings: PointSettings | null
): PointSettingsFormValues => ({
  singular: settings?.currencyName?.singular ?? "",
  plural: settings?.currencyName?.plural ?? "",
  gender: settings?.currencyName?.gender ?? null,
  rulesArticleId: settings?.rulesArticleId ?? null,
  eventPointsTitleTemplate: settings?.eventPointsMessageTemplates.titleTemplate ?? "",
  eventPointsAttendanceClauseTemplate: settings?.eventPointsMessageTemplates.attendanceClauseTemplate ?? "",
  eventPointsBonusClauseTemplate: settings?.eventPointsMessageTemplates.bonusClauseTemplate ?? "",
  referralShareText: settings?.referralShareText ?? "",
});

type PointCurrencyNameFields = Pick<PointSettingsFormValues, "singular" | "plural" | "gender">;

/** Los tres vacíos = usar el nombre por defecto. */
const isCurrencyNameBlank = (values: PointCurrencyNameFields): boolean =>
  (values.singular ?? "").trim() === "" &&
  (values.plural ?? "").trim() === "" &&
  values.gender == null;

// Los tres juntos o ninguno, igual que el backend. Yup recorta antes de validar: un nombre con
// solo espacios cuenta como vacío.
const requiredUnlessDefaultName = (value: unknown, values: PointCurrencyNameFields): boolean =>
  isCurrencyNameBlank(values) || (typeof value === "string" && value.trim() !== "");

const nameSchema = Yup.string()
  .trim()
  .max(POINT_CURRENCY_NAME_MAX_LENGTH, yupConstants(POINT_CURRENCY_NAME_MAX_LENGTH).max)
  .test("required-unless-default-name", yupConstants().required, function (value) {
    return requiredUnlessDefaultName(value, this.parent);
  });

// Cada plantilla es independiente: vacía es válida (el backend usa su default puntual), no hace
// falta la lógica de "los 3 juntos o ninguno" que sí aplica al nombre de los puntos.
const eventPointsTemplateSchema = Yup.string()
  .trim()
  .max(
    EVENT_POINTS_MESSAGE_TEMPLATE_MAX_LENGTH,
    yupConstants(EVENT_POINTS_MESSAGE_TEMPLATE_MAX_LENGTH).max
  );

// Independiente, como las plantillas: vacío es válido (el backend usa su default).
const referralShareTextSchema = Yup.string()
  .trim()
  .max(REFERRAL_SHARE_TEXT_MAX_LENGTH, yupConstants(REFERRAL_SHARE_TEXT_MAX_LENGTH).max);

export const pointSettingsValidationSchema = Yup.object({
  singular: nameSchema,
  plural: nameSchema,
  gender: Yup.string()
    .nullable()
    .test("required-unless-default-name", yupConstants().required, function (value) {
      return requiredUnlessDefaultName(value, this.parent);
    }),
  eventPointsTitleTemplate: eventPointsTemplateSchema,
  eventPointsAttendanceClauseTemplate: eventPointsTemplateSchema,
  eventPointsBonusClauseTemplate: eventPointsTemplateSchema,
  referralShareText: referralShareTextSchema,
});

/** Vacía (tras recortar espacios) manda `null`, que es "usar el default de esa plantilla". */
const toTemplateValue = (raw: string): string | null => {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
};

const buildEventPointsMessageTemplatesPayload = (
  values: PointSettingsFormValues
): ConfiguredEventPointsMessageTemplates => ({
  titleTemplate: toTemplateValue(values.eventPointsTitleTemplate),
  attendanceClauseTemplate: toTemplateValue(values.eventPointsAttendanceClauseTemplate),
  bonusClauseTemplate: toTemplateValue(values.eventPointsBonusClauseTemplate),
});

/**
 * Payload del PUT con los nombres recortados. Los tres vacíos mandan `null` (nombre por defecto).
 * Null si falta algún dato (la validación lo frena antes).
 */
export const buildUpdatePointSettingsPayload = (
  values: PointSettingsFormValues,
  settings: PointSettings | null
): UpdatePointSettingsPayload | null => {
  const eventPointsMessageTemplates = buildEventPointsMessageTemplatesPayload(values);
  const referralShareText = toTemplateValue(values.referralShareText);

  if (isCurrencyNameBlank(values)) {
    return {
      currencyName: null,
      rulesArticleId: values.rulesArticleId,
      eventPointsMessageTemplates,
      referralShareText,
    };
  }
  const singular = values.singular.trim();
  const plural = values.plural.trim();
  if (singular === "" || plural === "" || values.gender === null) {
    return null;
  }
  const keepsDefaultName =
    settings !== null &&
    settings.currencyName === null &&
    settings.effectiveCurrencyName.singular === singular &&
    settings.effectiveCurrencyName.plural === plural &&
    settings.effectiveCurrencyName.gender === values.gender;
  return {
    currencyName: keepsDefaultName ? null : { singular, plural, gender: values.gender },
    rulesArticleId: values.rulesArticleId,
    eventPointsMessageTemplates,
    referralShareText,
  };
};

export const NO_RULES_ARTICLE_OPTION_VALUE = "__none__";

export const toRulesArticleId = (dropdownValue: unknown): string | null =>
  typeof dropdownValue === "string" && dropdownValue !== NO_RULES_ARTICLE_OPTION_VALUE
    ? dropdownValue
    : null;

export const buildRulesArticleOptions = (
  articles: SupportArticleListItem[],
  selectedId: string | null,
  tenantId: string | null
): { label: string; value: string }[] => {
  const articleOptions = articles
    .filter(
      (article) =>
        tenantId !== null &&
        article.tenantId === tenantId &&
        (article.isActive || article.id === selectedId)
    )
    .map((article) => ({
      label: article.isActive ? article.title : `${article.title} (inactivo)`,
      value: article.id,
    }));

  return [
    { label: "Sin artículo (la app no muestra las reglas)", value: NO_RULES_ARTICLE_OPTION_VALUE },
    ...articleOptions,
  ];
};
